import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = '';

const DEFAULTS_PROJECTS = [
  {
    project_id: 'golf',
    name_fr: 'Le Golf de Songon', name_en: 'Songon Golf', short_name: 'GOLF',
    tagline_fr: 'Parcours 18 trous de standard international', tagline_en: '18-hole international course',
    description_fr: "Un projet d'exception alliant sport de prestige et nature préservée.",
    description_en: 'An exceptional project combining prestigious sport and preserved nature.',
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920&q=85',
    color: '#059669', url: 'https://legolfdesongon.com', is_external: true
  },
  {
    project_id: 'terres',
    name_fr: 'Les Terres de Songon', name_en: 'Songon Lands', short_name: 'TERRES',
    tagline_fr: 'Écosystème résidentiel intégré', tagline_en: 'Integrated residential ecosystem',
    description_fr: "Villas d'architecte et appartements de standing dans un cadre naturel.",
    description_en: 'Architect villas and upscale apartments in a natural setting.',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=85',
    color: '#047857', url: 'https://lesterresdesongon.com', is_external: true
  },
  {
    project_id: 'eastside',
    name_fr: 'Songon East-Side', name_en: 'Songon East-Side', short_name: 'EAST-SIDE',
    tagline_fr: 'Opportunité foncière stratégique', tagline_en: 'Strategic land opportunity',
    description_fr: 'Investissement foncier sécurisé à fort potentiel de valorisation.',
    description_en: 'Secured land investment with high appreciation potential.',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=85',
    color: '#065f46', url: '/songon-east-side', is_external: false
  },
  {
    project_id: 'extension',
    name_fr: 'Songon Extension', name_en: 'Songon Extension', short_name: 'EXTENSION',
    tagline_fr: 'Vision territoriale long terme', tagline_en: 'Long-term territorial vision',
    description_fr: 'Développement durable pensé pour les générations futures.',
    description_en: 'Sustainable development designed for future generations.',
    image: 'https://images.pexels.com/photos/5521699/pexels-photo-5521699.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85',
    color: '#064e3b', url: 'https://songonextension.com/', is_external: true
  }
];

const ProjectsShowcase = () => {
  const { language } = useLanguage();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [projects, setProjects] = useState(DEFAULTS_PROJECTS);

  useEffect(() => {
    fetch(`${API_URL}/api/public/homepage/projects`)
      .then(r => r.ok ? r.json() : ({ projects: DEFAULTS_PROJECTS }))
      .then(d => {
        if (d.projects && d.projects.length > 0) setProjects(d.projects);
      })
      .catch(() => { /* uses default projects */ });
  }, []);

  const getFlexValue = (index) => {
    if (hoveredIndex === null) return 1;
    return hoveredIndex === index ? 3 : 0.5;
  };

  return (
    <section 
      id="projets"
      className="relative h-screen w-full bg-stone-950 overflow-hidden"
      data-testid="projects-section"
    >
      {/* Section Title — Style Conservation Foncière */}
      <div className="absolute top-0 left-0 right-0 z-20 pt-8 pb-4 text-center"
        style={{ background: 'linear-gradient(to bottom, rgba(15,15,15,0.7) 0%, transparent 100%)' }}
      >
        <motion.h2
          className="font-display uppercase"
          style={{
            color: '#faf9f6',
            letterSpacing: '0.3em',
            textShadow: '0 2px 20px rgba(0,0,0,0.5)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="projects-title"
        >
          <span className="block text-xs sm:text-[11px] font-sans font-medium tracking-[0.3em] mb-3 opacity-60"
            style={{ color: '#c4a76c' }}
          >
            {language === 'fr' ? 'Nos Projets' : 'Our Projects'}
          </span>
          <span className="text-xl sm:text-2xl lg:text-3xl">
            {language === 'fr' ? 'Songon, une vision d\'ensemble' : 'Songon, a comprehensive vision'}
          </span>
        </motion.h2>
        <motion.p
          className="font-sans text-[10px] uppercase tracking-wider mt-3 opacity-30 text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {language === 'fr' ? '← Survolez pour explorer →' : '← Hover to explore →'}
        </motion.p>
      </div>

      {/* Vertical Slider Container */}
      <div className="flex h-full w-full">
        {projects.map((project, index) => {
          const name = language === 'fr' ? project.name_fr : project.name_en;
          const tagline = language === 'fr' ? project.tagline_fr : project.tagline_en;
          const description = language === 'fr' ? project.description_fr : project.description_en;

          return (
            <motion.div
              key={project.project_id}
              className="relative h-full overflow-hidden cursor-pointer"
              initial={{ flex: 1 }}
              animate={{ flex: getFlexValue(index) }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              data-testid={`project-panel-${index}`}
            >
              {/* Background Image */}
              <motion.div 
                className="absolute inset-0"
                initial={{ scale: 1.1 }}
                animate={{ scale: hoveredIndex === index ? 1 : 1.1 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              >
                <img
                  src={project.image}
                  alt={name}
                  className="w-full h-full object-cover"
                  style={{ filter: 'brightness(0.85) saturate(0.9)' }}
                  loading={index < 2 ? "eager" : "lazy"}
                />
                <motion.div 
                  className="absolute inset-0"
                  style={{ backgroundColor: 'rgba(30, 74, 26, 0.15)' }}
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: hoveredIndex === index ? 0.15 : 0.4 }}
                  transition={{ duration: 0.5 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent" />
              </motion.div>

              {/* Vertical Divider Line */}
              {index < projects.length - 1 && (
                <div className="absolute right-0 top-0 bottom-0 w-px bg-white/10 z-10" />
              )}

              {/* Project Number + Title — Center overlay (always visible when NOT hovered) */}
              <motion.div
                className="absolute inset-0 z-10 pointer-events-none flex flex-col items-center justify-center"
                animate={{ 
                  opacity: hoveredIndex === index ? 0 : (hoveredIndex === null ? 1 : 0.3)
                }}
                transition={{ duration: 0.3 }}
              >
                <span className="font-display text-5xl lg:text-6xl text-white/15 font-light mb-2">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3
                  className="font-display text-lg sm:text-xl lg:text-2xl text-white font-semibold leading-tight text-center px-3"
                  style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
                >
                  {name}
                </h3>
              </motion.div>

              {/* Expanded Content — visible on hover, replaces the title */}
              <AnimatePresence>
                {hoveredIndex === index && (
                  <motion.div 
                    className="absolute inset-0 z-10 flex flex-col justify-end"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Full gradient backdrop */}
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />

                    <div className="relative px-6 lg:px-10 pb-8 lg:pb-12">
                      {/* Project name */}
                      <motion.h3
                        className="font-display text-2xl lg:text-3xl text-white font-semibold leading-tight mb-3"
                        style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
                        initial={{ y: 15, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 15, opacity: 0 }}
                        transition={{ duration: 0.35, delay: 0.05 }}
                      >
                        {name}
                      </motion.h3>

                      {/* Emerald accent line */}
                      <motion.div
                        className="h-0.5 bg-emerald-500 mb-4"
                        style={{ maxWidth: '50px' }}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        exit={{ scaleX: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                      />

                      {/* Tagline */}
                      <motion.p 
                        className="text-emerald-400 font-sans text-sm lg:text-base font-medium uppercase tracking-wider mb-2"
                        initial={{ y: 15, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 15, opacity: 0 }}
                        transition={{ duration: 0.35, delay: 0.12 }}
                      >
                        {tagline}
                      </motion.p>

                      {/* Description */}
                      <motion.p 
                        className="text-white/70 font-sans text-sm lg:text-base leading-relaxed max-w-md mb-6"
                        initial={{ y: 15, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 15, opacity: 0 }}
                        transition={{ duration: 0.35, delay: 0.17 }}
                      >
                        {description}
                      </motion.p>

                      {/* CTA Button */}
                      <motion.div
                        initial={{ y: 15, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 15, opacity: 0 }}
                        transition={{ duration: 0.35, delay: 0.22 }}
                      >
                        {project.is_external ? (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-3 px-6 py-3 
                                       bg-emerald-600 text-white font-sans text-sm font-medium 
                                       uppercase tracking-wider
                                       transition-all duration-300
                                       hover:bg-emerald-500 hover:gap-4"
                            data-testid={`project-cta-${index}`}
                          >
                            {language === 'fr' ? 'Découvrir' : 'Discover'}
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </a>
                        ) : (
                          <Link
                            to={project.url}
                            className="group inline-flex items-center gap-3 px-6 py-3 
                                       bg-emerald-600 text-white font-sans text-sm font-medium 
                                       uppercase tracking-wider
                                       transition-all duration-300
                                       hover:bg-emerald-500 hover:gap-4"
                            data-testid={`project-cta-${index}`}
                          >
                            {language === 'fr' ? 'Explorer' : 'Explore'}
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </Link>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bottom Indicator Line */}
              <motion.div 
                className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: hoveredIndex === index ? 1 : 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                style={{ transformOrigin: 'left' }}
              />
            </motion.div>
          );
        })}
      </div>

    </section>
  );
};

export default ProjectsShowcase;
